/**
 * Section dans Préférences pour gérer le token API FDS Polyprint.
 *
 * UX :
 *   - À l'ouverture, on récupère l'état (configuré ou pas, version masquée)
 *   - Si configuré : on affiche la version masquée + bouton "Tester" / "Supprimer"
 *   - Si pas configuré : champ vide + bouton "Enregistrer"
 *
 * Le token complet n'est jamais re-affiché : si l'utilisateur veut le
 * changer, il doit le re-saisir. Sécurité ↑.
 */
import React from 'react';
import {
  getTokenStatus,
  setToken as apiSetToken,
  deleteToken as apiDeleteToken,
  testToken as apiTestToken,
} from '../api/regulatoryClient';


export default function RegulatoryTokenSection() {
  // status démarre par défaut à 'pas configuré' → formulaire visible
  // immédiatement même si l'API backend n'est pas joignable (le user voit
  // le champ et peut au moins comprendre où coller son token).
  const [status, setStatus] = React.useState({ configured: false, masked: null, source: 'aucun' });
  const [input, setInput] = React.useState('');
  const [busy, setBusy] = React.useState(false);
  const [info, setInfo] = React.useState(null);
  const [error, setError] = React.useState(null);
  const [editing, setEditing] = React.useState(false);
  const [backendUnavailable, setBackendUnavailable] = React.useState(false);

  React.useEffect(() => {
    getTokenStatus()
      .then((s) => { setStatus(s); setBackendUnavailable(false); })
      .catch((e) => {
        setStatus({ configured: false, source: 'aucun' });
        // 404 → endpoint pas encore en prod (backend pas redéployé). Le détail
        // d'exploitation reste en console : le bandeau affiché à l'utilisateur ne
        // lui dit que ce qui ne marche pas et qui prévenir.
        if (e?.status === 404) {
          console.warn(
            "[réglementaire] La route de gestion du jeton répond 404 : elle est absente du backend déployé.",
          );
          setBackendUnavailable(true);
        }
      });
  }, []);

  // Le minuteur en cours, pour qu'un message GARDÉ ne soit pas effacé par le
  // minuteur d'un message précédent qui arriverait à échéance après lui.
  const minuteurRef = React.useRef(null);
  React.useEffect(() => () => clearTimeout(minuteurRef.current), []);

  /**
   * `garder` : le message reste jusqu'au message suivant. C'est le cas du rapport
   * de test, qui se recopie pour Polyprint — effacé au bout de 5 s, il ne
   * pouvait être ni relu ni transmis.
   */
  const flash = (kind, msg, { garder = false } = {}) => {
    clearTimeout(minuteurRef.current);
    if (kind === 'info') { setInfo(msg); setError(null); }
    else { setError(msg); setInfo(null); }
    if (!garder) {
      minuteurRef.current = setTimeout(() => { setInfo(null); setError(null); }, 5000);
    }
  };

  const handleSave = async () => {
    if (!input.trim()) { flash('error', "Le champ est vide : collez votre jeton avant d'enregistrer."); return; }
    setBusy(true);
    try {
      const s = await apiSetToken(input.trim());
      setStatus(s);
      setInput('');
      setEditing(false);
      flash('info', 'Jeton enregistré.');
    } catch (e) {
      flash('error', e.message || "L'enregistrement a échoué : réessayez, et prévenez l'administrateur si cela persiste.");
    } finally {
      setBusy(false);
    }
  };

  const handleDelete = async () => {
    if (!confirm('Supprimer le jeton API FDS ? Vous ne pourrez plus rafraîchir les fiches réglementaires.')) return;
    setBusy(true);
    try {
      const s = await apiDeleteToken();
      setStatus(s);
      flash('info', 'Jeton supprimé.');
    } catch (e) {
      flash('error', e.message || "La suppression a échoué : réessayez, et prévenez l'administrateur si cela persiste.");
    } finally {
      setBusy(false);
    }
  };

  const handleTest = async () => {
    setBusy(true);
    try {
      const r = await apiTestToken();
      // Le rapport détaille chaque porte de l'API, codes compris : il reste à
      // l'écran pour être relu et transmis à Polyprint.
      if (r.ok) flash('info', r.detail || 'Connexion établie.', { garder: true });
      else flash('error', r.detail || 'Jeton refusé : vérifiez celui que Polyprint vous a communiqué.', { garder: true });
    } catch (e) {
      flash('error', e.message || "Le test n'a pas pu aboutir : réessayez dans un instant.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-3">
      <div className="flex items-center gap-2 mb-1">
        <div className="w-1.5 h-1.5 rounded-full bg-orange-500/70"></div>
        <p className="text-[10px] text-orange-300/80 font-bold uppercase tracking-[0.15em]">
          Récupération informations réglementaires
        </p>
      </div>

      <div className="bg-black/30 border border-orange-500/20 rounded-xl p-4 shadow-inner space-y-3">
        <div className="text-[9px] font-mono text-white/40 leading-relaxed">
          Jeton API <span className="text-orange-300/90">Polyprint FDS</span> · Service : <span className="text-white/60">fds.polyprint-studio.fr</span>
        </div>

        {backendUnavailable && (
          <div className="p-2 rounded-md bg-amber-500/10 border border-amber-500/30 text-[10px] font-mono text-amber-300">
            ⚠ La gestion du jeton n'est pas disponible sur ce serveur : vous ne pouvez ni
            l'enregistrer ni le vérifier pour le moment. Contactez l'administrateur, puis réessayez.
          </div>
        )}

        {/* Le jeton de l'ATELIER prend le relais : ne rien dire ferait afficher
            huit points de suspension à quelqu'un qui n'a rien enregistré, avec
            un bouton « Supprimer » qui ne supprimerait rien. */}
        {status.source === 'atelier' && !editing && (
          <div className="p-2.5 rounded-md bg-white/5 border border-white/10 text-[10px] font-mono text-white/60 leading-relaxed">
            Le jeton de <span className="text-orange-300/90">l'atelier</span> est employé pour ce compte :
            vous n'avez rien à saisir, l'import réglementaire fonctionne.
            {status.masked === '<illisible>' && (
              <span className="block mt-1 text-amber-300/90">
                Votre jeton personnel, lui, n'est plus lisible : saisissez-le à nouveau si vous
                voulez interroger l'API sous votre propre identité.
              </span>
            )}
          </div>
        )}

        {/* Affichage état */}
        {status.configured && status.source === 'personnel' && !editing ? (
          <div className="flex items-center gap-2">
            <div className="flex-1 bg-black/30 border border-white/10 rounded-lg px-3 py-2 text-[11px] font-mono text-white/85 tracking-wider">
              {status.masked || '••••••••'}
            </div>
            <button
              onClick={() => { setEditing(true); setInput(''); }}
              disabled={busy}
              className="px-3 py-2 bg-white/5 hover:bg-white/15 border border-white/10 rounded-lg text-[10px] font-mono uppercase tracking-widest text-white/70 transition-all disabled:opacity-50"
              title="Modifier le jeton"
            >
              Modifier
            </button>
          </div>
        ) : (
          <div className="flex items-center gap-2">
            <input
              type="password"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              placeholder="asfds_XXXXXXXXXXXXXXXXXXXX"
              className="flex-1 bg-black/30 border border-white/10 rounded-lg px-3 py-2 text-[11px] font-mono text-white/90 placeholder-white/25 outline-none focus:border-orange-500/40 transition-colors tracking-wider"
            />
            <button
              onClick={handleSave}
              disabled={busy}
              className="px-3 py-2 bg-orange-500/15 hover:bg-orange-500/25 border border-orange-500/40 hover:border-orange-500/60 rounded-lg text-[10px] font-mono uppercase tracking-widest text-orange-300 transition-all disabled:opacity-50"
            >
              {busy ? '…' : 'Enregistrer'}
            </button>
            {editing && (
              <button
                onClick={() => { setEditing(false); setInput(''); }}
                disabled={busy}
                className="px-2 py-2 text-white/40 hover:text-white text-[10px] font-mono uppercase tracking-widest transition-colors"
              >
                Annuler
              </button>
            )}
          </div>
        )}

        {/* Actions secondaires */}
        {status?.configured && status.source === 'personnel' && !editing && (
          <div className="flex gap-2">
            <button
              onClick={handleTest}
              disabled={busy}
              className="flex-1 py-1.5 bg-white/5 hover:bg-white/10 border border-white/10 rounded-md text-[9px] font-mono uppercase tracking-widest text-white/60 hover:text-white transition-all disabled:opacity-50"
            >
              Tester la connexion
            </button>
            <button
              onClick={handleDelete}
              disabled={busy}
              className="px-3 py-1.5 bg-red-500/10 hover:bg-red-500/20 border border-red-500/30 hover:border-red-500/50 rounded-md text-[9px] font-mono uppercase tracking-widest text-red-300 transition-all disabled:opacity-50"
            >
              Supprimer
            </button>
          </div>
        )}

        {info && (
          <div className="p-2 rounded-md bg-green-500/10 border border-green-500/30 text-[10px] font-mono text-green-300 whitespace-pre-line select-text">
            ✓ {info}
          </div>
        )}
        {error && (
          <div className="p-2 rounded-md bg-red-500/10 border border-red-500/30 text-[10px] font-mono text-red-300 whitespace-pre-line select-text">
            ⚠ {error}
          </div>
        )}

        <p className="text-[9px] font-mono text-white/30 leading-relaxed pt-1">
          Le jeton est chiffré côté serveur et ne transite jamais en clair.
          Pour le récupérer ou le renouveler, contactez Polyprint.
        </p>
      </div>
    </div>
  );
}
